/**
 * MTCustomSelect - Multi-territory single-select dropdown. Mirrors India's
 * CustomSelect visual style but is a separate MT-owned component so India code
 * is never touched.
 *
 * Difference from India CustomSelect:
 * - Long option text WRAPS onto multiple lines in the dropdown (instead of
 *   truncating / stretching the menu), while the selected value in the closed
 *   trigger stays on one line with an ellipsis.
 * - The dropdown menu width is capped so a very long option can't stretch it
 *   across the whole page.
 * - Optional sticky in-dropdown search box (searchable) to filter options,
 *   mirroring MTMultiSelect's search UX.
 */
import { KeyboardArrowDown, Search as SearchIcon } from '@mui/icons-material'
import {
    Box,
    FormControl,
    InputAdornment,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { useMemo, useRef, useState } from 'react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = any

export type MTCustomSelectProps = {
  label?: string
  value?: string
  onChange: (e: { target: { value: string } }) => void
  options?: Option[]
  getOptionLabel?: (option: Option) => string
  getOptionValue?: (option: Option) => string
  placeholder?: string
  required?: boolean
  error?: boolean
  helperText?: string
  disabled?: boolean
  fullWidth?: boolean
  /** When true, renders a sticky search box at the top to filter options. */
  searchable?: boolean
  searchPlaceholder?: string
}

export function MTCustomSelect({
  label,
  value,
  onChange,
  options = [],
  getOptionLabel = (option: Option) => option,
  getOptionValue = (option: Option) => option,
  placeholder = 'Select an option',
  required = false,
  error = false,
  helperText = '',
  disabled = false,
  fullWidth = false,
  searchable = false,
  searchPlaceholder = 'Search',
  ...props
}: MTCustomSelectProps) {
  const [searchTerm, setSearchTerm] = useState('')
  // Ref to the FormControl so the dropdown menu can match the trigger's width
  // exactly and stay aligned under the select field.
  const controlRef = useRef<HTMLDivElement>(null)
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined)

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options
    const q = searchTerm.trim().toLowerCase()
    return options.filter(opt =>
      String(getOptionLabel(opt) ?? '')
        .toLowerCase()
        .includes(q)
    )
  }, [searchable, searchTerm, options, getOptionLabel])
  // Full label of the current value, used for the native hover tooltip on the
  // (truncated) closed trigger.
  const selectedOptionForTitle = options.find(
    opt => getOptionValue(opt) === value
  )
  const selectedLabelText =
    value && selectedOptionForTitle
      ? String(getOptionLabel(selectedOptionForTitle))
      : undefined

  return (
    <Box
      sx={{
        minWidth: fullWidth ? 0 : 225,
        flex: fullWidth ? 1 : '0 0 auto',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {label && (
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            mb: 1,
            lineHeight: '140%',
          }}
        >
          {label}
          {required && (
            <span style={{ color: 'var(--forbrand-error)' }}>*</span>
          )}
        </Typography>
      )}
      <FormControl
        ref={controlRef}
        fullWidth
        size="small"
        error={error}
        disabled={disabled}
      >
        <Tooltip
          title={selectedLabelText || ''}
          arrow
          placement="top"
          disableInteractive
        >
          <Select
            value={value || ''}
            onChange={onChange as any}
            IconComponent={KeyboardArrowDown}
            displayEmpty
            onOpen={() =>
              setMenuWidth(controlRef.current?.offsetWidth ?? undefined)
            }
            // Native title as a fallback; the wrapping MUI Tooltip above is the
            // primary hover tooltip (reliable because it wraps the whole Select).
            SelectDisplayProps={{ title: selectedLabelText }}
            renderValue={selected => {
              const sel = selected as string
              if (!sel || sel === '') {
                return <span style={{ color: '#999' }}>{placeholder}</span>
              }
              const selectedOption = options.find(
                opt => getOptionValue(opt) === sel
              )
              const labelText = selectedOption
                ? getOptionLabel(selectedOption)
                : sel
              // Closed trigger: single line + ellipsis (fixed height).
              return (
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {labelText}
                </span>
              )
            }}
            sx={{
              backgroundColor: disabled ? 'var(--bg-disabled)' : '#fff',
              fontSize: '14px',
              height: '48px',
              width: fullWidth ? '100%' : '225px',
              borderRadius: 'unset',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: error
                  ? 'var(--forbrand-error)'
                  : 'var(--forbrand-color)',
                borderWidth: '1px',
              },
              '& .MuiSelect-select': {
                fontSize: '14px',
                color: '#374151',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiSelect-icon': { color: '#6b7280', fontSize: '20px' },
              '&.Mui-disabled': { backgroundColor: 'var(--bg-disabled)' },
            }}
            onClose={() => setSearchTerm('')}
            MenuProps={{
              // Keep focus available for the search box rather than the first item.
              autoFocus: false,
              // Anchor the menu to the bottom-left of the trigger so it lines up
              // flush with the select field instead of floating off to the side.
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: {
                  boxShadow:
                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                  border: '1px solid var(--border-strong)',
                  mt: 0.5,
                  maxHeight: '250px',
                  boxSizing: 'border-box',
                  // Match the menu width to the trigger so options align flush
                  // under the field (no overflow past the select box). MUI would
                  // otherwise size the popover from its own content.
                  width: menuWidth,
                  minWidth: menuWidth ? `${menuWidth}px !important` : undefined,
                  maxWidth: menuWidth ? `${menuWidth}px !important` : undefined,
                },
              },
            }}
            {...props}
          >
            {searchable && (
              <ListSubheader
                sx={{ p: 1, backgroundColor: '#fff', lineHeight: 'normal' }}
                onKeyDown={e => e.stopPropagation()}
              >
                <TextField
                  size="small"
                  autoFocus
                  fullWidth
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          fontSize="small"
                          sx={{ color: '#6b7280' }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': { fontSize: '14px' },
                    // Brand-styled border (orange focus) instead of MUI blue.
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: '#d0d0d0',
                      },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: '#a0a0a0',
                      },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                      {
                        borderColor: 'var(--forbrand-color)',
                        borderWidth: '1px',
                      },
                  }}
                />
              </ListSubheader>
            )}

            {/* Placeholder row only when NOT actively searching. */}
            {placeholder && !(searchable && searchTerm.trim()) && (
              <MenuItem value="" disabled>
                <span style={{ color: '#999' }}>{placeholder}</span>
              </MenuItem>
            )}
            {filteredOptions.map((option, index) => (
              <MenuItem
                key={index}
                value={getOptionValue(option)}
                sx={{
                  fontSize: '14px',
                  color: '#374151',
                  py: 1.5,
                  px: 2,
                  // Long option text wraps onto the next line instead of
                  // truncating / stretching the menu.
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  '&:hover': { backgroundColor: '#f9fafb' },
                  '&.Mui-selected': {
                    backgroundColor: '#f3f4f6',
                    '&:hover': { backgroundColor: '#e5e7eb' },
                  },
                }}
              >
                {getOptionLabel(option)}
              </MenuItem>
            ))}

            {searchable &&
              searchTerm.trim() &&
              filteredOptions.length === 0 && (
                <MenuItem disabled sx={{ fontSize: '14px', color: '#999' }}>
                  No matches found
                </MenuItem>
              )}
          </Select>
        </Tooltip>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? '#d32f2f' : '#666',
              mt: 0.5,
              fontSize: '0.75rem',
            }}
          >
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  )
}

export default MTCustomSelect
